import { STORES, type StorageDriver, type StorageSnapshot, type StorageTransaction, type StorageUpgrade, type StoreName } from './driver.ts';

const SCHEMA_VERSION = 1;
const DATABASE_PREFIX = 'dissidium';

export const databaseName = (namespace: string): string => `${DATABASE_PREFIX}:${namespace}:rolling-save`;

const request = <T>(source: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  source.onsuccess = () => resolve(source.result);
  source.onerror = () => reject(source.error ?? new Error('The IndexedDB request failed'));
});

function storesOf(tx: IDBTransaction): Record<StoreName, IDBObjectStore> {
  return Object.fromEntries(STORES.map(store => [store, tx.objectStore(store)])) as Record<StoreName, IDBObjectStore>;
}

async function entriesOf(store: IDBObjectStore): Promise<Array<{ key: string; value: unknown }>> {
  const [keys, values] = await Promise.all([request(store.getAllKeys()), request(store.getAll())]);
  return keys.map((key, index) => ({ key: String(key), value: values[index] }));
}

/** The real browser path: writes commit through IndexedDB transactions and roll back as a unit. */
export class IndexedDbDriver implements StorageDriver {
  readonly name: string;
  #factory: IDBFactory;
  #database: IDBDatabase | null = null;

  constructor(factory: IDBFactory, name: string) {
    this.#factory = factory;
    this.name = name;
  }

  static fromGlobal(namespace: string): IndexedDbDriver {
    if (typeof globalThis.indexedDB === 'undefined') throw new Error('This browser has no IndexedDB, so no run can be saved.');
    return new IndexedDbDriver(globalThis.indexedDB, databaseName(namespace));
  }

  async open(upgrade: (schema: StorageUpgrade) => void): Promise<void> {
    const handle = this.#factory.open(this.name, SCHEMA_VERSION);
    this.#database = await new Promise<IDBDatabase>((resolve, reject) => {
      handle.onupgradeneeded = () => upgrade({
        createStore: store => { if (!handle.result.objectStoreNames.contains(store)) handle.result.createObjectStore(store); },
      });
      handle.onsuccess = () => resolve(handle.result);
      handle.onerror = () => reject(handle.error ?? new Error(`Cannot open the rolling save database ${this.name}`));
      handle.onblocked = () => reject(new Error(`Another tab holds an older version of ${this.name}; close it and try again.`));
    });
  }

  close(): void {
    this.#database?.close();
    this.#database = null;
  }

  async read<T>(body: (snapshot: StorageSnapshot) => T): Promise<T> {
    const database = this.#require();
    const tx = database.transaction([...STORES], 'readonly');
    const stores = storesOf(tx);
    const loaded = new Map<StoreName, Array<{ key: string; value: unknown }>>();
    for (const store of STORES) loaded.set(store, await entriesOf(stores[store]));
    return body({
      get: (store, key) => loaded.get(store)?.find(entry => entry.key === key)?.value,
      all: store => loaded.get(store) ?? [],
    });
  }

  async write(body: (tx: StorageTransaction) => Promise<void>): Promise<void> {
    const database = this.#require();
    const tx = database.transaction([...STORES], 'readwrite', { durability: 'strict' });
    const stores = storesOf(tx);
    let settled = false;
    const completed = new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => { settled = true; resolve(); };
      tx.onabort = () => { settled = true; reject(tx.error ?? new Error('The rolling-save transaction was rolled back')); };
      tx.onerror = () => { if (!settled) { settled = true; reject(tx.error ?? new Error('The rolling-save transaction failed')); } };
    });
    try {
      await body({
        get: async (store, key) => (await request(stores[store].get(key)) as unknown),
        all: store => entriesOf(stores[store]),
        put: async (store, key, value) => { await request(stores[store].put(value, key)); },
        remove: async (store, key) => { await request(stores[store].delete(key)); },
      });
    } catch (error) {
      try { tx.abort(); } catch { /* already aborting */ }
      await completed.catch(() => undefined);
      throw error;
    }
    await completed;
  }

  #require(): IDBDatabase {
    if (!this.#database) throw new Error(`The rolling save database ${this.name} is not open`);
    return this.#database;
  }
}
